import { CommonModule, Location } from '@angular/common';
import { Component, ViewEncapsulation, computed, inject, input } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ImageZoomDirective } from '@app/components/zoom/image-zoom-directive/image-zoom.directive';
import { MbDocument } from '@app/article/mercedes-articles/mercedes-articles.component';
import { TranslocoPipe } from '@jsverse/transloco';
import { MarkdownModule } from 'ngx-markdown';

@Component({
  selector: 'app-mercedes-renderer',
  templateUrl: './mercedes-renderer.component.html',
  styleUrls: ['./mercedes-renderer.component.scss'],
  imports: [CommonModule, TranslocoPipe, MarkdownModule, ImageZoomDirective],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class MercedesRendererComponent {
  data = input<MbDocument | string | undefined>();

  private sanitizer = inject(DomSanitizer);
  private location = inject(Location);

  textData = computed(() => {
    const data = this.data();
    if (!data || typeof data === 'object') {
      return undefined;
    }

    return data;
  });

  htmlData = computed(() => {
    const data = this.data();
    if (!data || typeof data === 'string' || (typeof data === 'object' && !data.document)) {
      return undefined;
    }
    if (typeof data === 'object' && data.document) {
      // return this.sanitizer.bypassSecurityTrustHtml(data.document);
      const withCorrectLinks = data.document?.replace(/href="([^"]*)"/g, (match, href) => {
        if (href.startsWith('http') || href.startsWith('https') || href.startsWith('#')) {
          return match;
        }
        return `href="${this._fixDocumentUrl(href)}"`;
      });
      return this.sanitizer.bypassSecurityTrustHtml(withCorrectLinks);
    }
    if (typeof data === 'object' && data.svg) {
      return this.sanitizer.bypassSecurityTrustHtml(data.svg.content);
    }

    return undefined;
  });

  handleClick(event: Event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter') {
        return;
      }
    }

    const target = event.target as HTMLElement;
    const isInternalLink = target.tagName === 'A' && target.getAttribute('href')?.startsWith('#');

    if (isInternalLink) {
      event.preventDefault();
      const hash = target.getAttribute('href');
      if (!hash) return;

      this.scrollToInternalLink(hash);
    }
  }

  private scrollToInternalLink(hash: string) {
    const currentUrl = this.location.path().split('#')[0];
    this.location.go(currentUrl + hash);

    const elementId = hash.substring(1);
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });

      if (element.tagName === 'IMG') {
        this.highlight(element, 'border');
      } else {
        this.highlight(element, 'background');
      }
    }
  }

  private _fixDocumentUrl(url: string) {
    const params = new URL(url || '', window.location.origin).searchParams;
    params.set('articleView', true.toString());
    return `${window.location.pathname}?${params.toString()}`;
  }

  private highlight(element: HTMLElement, type: 'border' | 'background' = 'border', duration = 2000) {
    const highlightClass = type === 'border' ? 'highlight-border' : 'highlight-background';

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });

    element.classList.add(highlightClass);

    setTimeout(() => {
      element.classList.remove(highlightClass);
    }, duration);
  }
}
